import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IFavoriteButtonWidget } from '#/types/widgets';
import { useAPI, useClickEvent, useExternalControl } from '#/hooks';
import userSlice from '#/store/accessors/user';
import { setUserFavoritesAction } from '#/store/actions/actions';
import debounce from '#/utils/debounce';
import styles from './favoriteButton.module.css';

interface IPropTypes {
  desc: IFavoriteButtonWidget;
}

const FavoriteButton: React.FC<IPropTypes> = ({ desc }) => {
  const { id, iconSize, objectId, contentType, autoFocus, isMainTarget } = desc;

  const API = useAPI();

  const dispatch = useDispatch();
  const storeValue = useSelector((state) =>
    userSlice.selectFavoriteById(state, objectId),
  );

  const ref = useExternalControl() as React.LegacyRef<HTMLButtonElement>;

  const [_isFavorite, setIsFavorite] = useState<boolean>(storeValue);

  const postFavorite = useCallback(
    (value: boolean) => {
      if (value === storeValue) {
        return;
      }

      const content = contentType === 'tv' ? '/tv' : '';
      const action = value ? 'add' : 'delete';
      const path = `user/favorite${content}/${action}`;
      const payload = value
        ? { object_id: objectId }
        : { object_ids: objectId };

      API.interactiveAPI
        .japiPost(path, payload)
        .then(() => {
          dispatch(setUserFavoritesAction({ [objectId]: value }));
        })
        .catch((error: Error) => {
          console.warn('post user favorites error', error);
          setIsFavorite(storeValue);
        });
    },
    [storeValue, objectId, contentType, API.interactiveAPI, dispatch],
  );

  const debouncedToggleHandler = useMemo(
    () => debounce(postFavorite, 500),
    [postFavorite],
  );

  const onClick = useClickEvent(desc);

  const _onClick = useCallback((event: React.MouseEvent) => {
    setIsFavorite((current) => {
      debouncedToggleHandler(!current);
      return !current;
    });
    onClick(event);
  }, [debouncedToggleHandler, onClick]);

  useEffect(() => {
    if (storeValue !== _isFavorite) {
      setIsFavorite(storeValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeValue]);

  const etTarget = useMemo(() => {
    return JSON.stringify({
      program_id: Number(objectId),
    })
  }, [ objectId ]);

  return (
    <button
      ref={ref}
      id={id}
      className={styles.favoriteButton}
      data-value={_isFavorite}
      data-auto-focus={autoFocus}
      data-main-target={isMainTarget}
      onClick={_onClick}
      data-et-event={ `click:${_isFavorite ? 'favourites_remove' : 'favourites_add'}`}
      data-et-el-code='favourites'
      data-et-target={etTarget}
    >
      {_isFavorite ? (
        <svg
          width={iconSize}
          height={iconSize}
          viewBox={`0 0 ${iconSize} ${iconSize}`}
          fill="none"
          data-role="icon"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M10.0023 3H14.0023C14.9391 3.00435 15.6274 3.0306 16.1507 3.10231C16.6896 3.17615 17.1263 3.30693 17.5155 3.56589L17.518 3.56752C17.8793 3.80941 18.1879 4.1176 18.4356 4.48257L18.4415 4.49127C18.7 4.88089 18.8286 5.31634 18.9007 5.85282C18.9707 6.37344 18.9957 7.05968 19 7.99572L19 8.00036L19 18.997C19 20.692 17.0222 21.6179 15.7196 20.5328L12 18L8.28037 20.5328C6.97777 21.6179 5 20.692 5 18.997L5.00002 7.99569C5.00437 7.0593 5.03063 6.37128 5.10237 5.84815C5.17623 5.30952 5.30708 4.87303 5.56612 4.48391L5.56776 4.48145C5.80972 4.1203 6.11806 3.81177 6.48315 3.56417L6.49186 3.55827C6.88161 3.29989 7.31728 3.17135 7.85394 3.09928C8.37474 3.02933 9.06132 3.00436 9.99768 3.00001L10.0023 3Z"
            fill="white"
          />
        </svg>
      ) : (
        <svg
          width={iconSize}
          height={iconSize}
          viewBox={`0 0 ${iconSize} ${iconSize}`}
          fill="none"
          data-role="icon"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M10.0023 3H14.0023C14.9391 3.00435 15.6274 3.0306 16.1507 3.10231C16.6896 3.17615 17.1263 3.30693 17.5155 3.56589L17.518 3.56752C17.8793 3.80941 18.1879 4.1176 18.4356 4.48257L18.4415 4.49127C18.7 4.88089 18.8286 5.31634 18.9007 5.85282C18.9707 6.37344 18.9957 7.05968 19 7.99572L19 8.00036L19 18.997C19 20.692 17.0222 21.6179 15.7196 20.5328L12 18L8.28037 20.5328C6.97777 21.6179 5 20.692 5 18.997L5.00002 7.99569C5.00437 7.0593 5.03063 6.37128 5.10237 5.84815C5.17623 5.30952 5.30708 4.87303 5.56612 4.48391L5.56776 4.48145C5.80972 4.1203 6.11806 3.81177 6.48315 3.56417L6.49186 3.55827C6.88161 3.29989 7.31728 3.17135 7.85394 3.09928C8.37474 3.02933 9.06132 3.00436 9.99768 3.00001L10.0023 3ZM10.0047 4.99921C9.07467 5.00357 8.50163 5.02946 8.12025 5.08068C7.76386 5.12855 7.65082 5.18954 7.60108 5.22193C7.44727 5.3271 7.32525 5.4514 7.2304 5.59261C7.19731 5.64256 7.13375 5.7557 7.08384 6.11967C7.03147 6.50155 7.00437 7.07497 7.00002 8.00267L7 18.997L10.7196 16.4642C11.4613 15.8463 12.5387 15.8463 13.2804 16.4642L17 18.997L17 8.00275C16.9957 7.07305 16.9697 6.50027 16.9185 6.11901C16.8706 5.76279 16.8096 5.6498 16.7772 5.60005C16.672 5.44629 16.5476 5.32431 16.4063 5.22948C16.3564 5.1964 16.2431 5.13288 15.8791 5.083C15.4971 5.03065 14.9234 5.00356 13.9954 4.99921H10.0047Z"
            fill="white"
          />
        </svg>
      )}
    </button>
  );
};

FavoriteButton.displayName = 'FavoriteButton';

export default React.memo(FavoriteButton);
